import React, { useEffect, useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import SwirlBackground from './SwirlBackground';
import LoadingScreen from './LoadingScreen';
import './LandingPage.css';


const CURRENCIES = ['$', '€', '£', '¥', '₹', '₿', 'Ξ', 'Ł', '₣', '₱', '₩', '₫', '₪', '₺', '₼', '₾', '₸', '₺', '₴', '₵', '₦', '₠', '₢', '₯', '₳', '₶', '₷', '₸', '₹', '円', '元', '₩', '₺', '₽', '₣', '₤', '₧'];

const AuthModal = ({ type, onClose, onSwitch }) => {
  const { signin, signup, signinWithGoogle, resetPassword, loading } = useContext(AuthContext);
  // Pre-fill the email if we know the user's last session — saves them
  // re-typing on a "your session expired, sign back in" bounce.
  const [email, setEmail] = useState(() => {
    if (type === 'signin') {
      try { return localStorage.getItem('last_session_email') || ''; } catch { return ''; }
    }
    return '';
  });
  const [password, setPassword] = useState('');
  const [localErr, setLocalErr] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const navigate = useNavigate();

  const handleAuth = async (e) => {
    e.preventDefault();
    setLocalErr('');
    setSuccessMsg('');
    try {
      if (type === 'signin') {
        await signin(email, password);
        navigate('/dashboard');
      } else if (type === 'signup') {
        await signup(email, password);
        navigate('/dashboard');
      } else if (type === 'forgotpw') {
        await resetPassword(email);
        setSuccessMsg('Recovery email sent! Please check your inbox.');
      }
    } catch (err) {
      setLocalErr(err.response?.data?.error || err.message || 'Action failed');
    }
  };

  const handleGoogle = async () => {
    try {
      await signinWithGoogle();
    } catch (err) { }
  };

  const getTitle = () => {
    if (type === 'signin') return 'Welcome Back';
    if (type === 'signup') return 'Create Account';
    return 'Reset Password';
  };

  const getSubtitle = () => {
    if (type === 'signin') return 'Sign in to access your finances';
    if (type === 'signup') return 'Start your financial journey today';
    return 'Enter your email to recover your account';
  };

  return (
    <div className="lp-modal-overlay" onClick={onClose}>
      <div className={`lp-modal-card lp-modal-card-${type}`} onClick={e => e.stopPropagation()}>
        {loading && (
          <div className="lp-modal-loading-overlay">
            <div className="lp-modal-spinner"></div>
          </div>
        )}
        <button className="lp-modal-close" onClick={onClose}>×</button>
        
        <div key={type + (successMsg ? '-success' : '')} className="lp-modal-animate-wrap">
          <h2>{getTitle()}</h2>
          <p>{getSubtitle()}</p>

          {localErr && <div className="lp-modal-error">{localErr}</div>}
          {successMsg && <div className="lp-modal-success">{successMsg}</div>}

          <form onSubmit={handleAuth} className="lp-modal-form">
            <input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            {type !== 'forgotpw' && (
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            )}
            {type === 'signin' && (
              <div className="lp-modal-forgot">
                <button type="button" onClick={() => onSwitch('forgotpw')}>Forgot Password?</button>
              </div>
            )}
            <button type="submit" className="lp-modal-submit" disabled={loading}>
              {loading ? 'Processing...' : (type === 'signin' ? 'Sign In' : type === 'signup' ? 'Sign Up' : 'Send Reset Link')}
            </button>
          </form>

          {type !== 'forgotpw' && (
            <>
              <div className="lp-modal-divider"><span>or</span></div>
              <button onClick={handleGoogle} className="lp-modal-google">
                <svg className="lp-modal-google-icon" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                </svg>
                Continue with Google
              </button>
            </>
          )}

          <p className="lp-modal-switch">
            {type === 'signin' ? "Don't have an account? " : type === 'signup' ? "Already have an account? " : ""}
            {type === 'forgotpw' ? (
              <button onClick={() => onSwitch('signin')}>Back to Sign In</button>
            ) : (
              <button onClick={() => onSwitch(type === 'signin' ? 'signup' : 'signin')}>
                {type === 'signin' ? 'Sign Up' : 'Sign In'}
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};


export default function LandingPage() {
  const [scrollPos, setScrollPos] = useState(0);
  const [time, setTime] = useState(0);
  const [authModal, setAuthModal] = useState({ show: false, type: 'signin' });
  const smoothScroll = useRef(0);
  const heroRef = useRef(null);
  const logoRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useContext(AuthContext);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    let frame;
    const animate = (t) => {
      setTime(t / 1000);

      // Clamp to >= 0 so mobile overscroll/bounce at the top can't drive scrollPos negative.
      const target = Math.max(0, window.scrollY);
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        // On mobile: track scroll position 1:1 so the circle only moves when
        // the user is actively scrolling — no inertia, no catch-up animation.
        smoothScroll.current = target;
      } else {
        smoothScroll.current += (target - smoothScroll.current) * 0.2;
        smoothScroll.current = Math.max(0, smoothScroll.current);
      }
      setScrollPos(smoothScroll.current);

      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);



  const isMobileView = window.innerWidth < 768;
  const activeCurrencies = isMobileView 
    ? CURRENCIES.filter((c, i) => i % 2 === 0 || c === '$' || c === '₹').slice(0, 20)
    : CURRENCIES;

  // Calculate icon positions and styles based on scroll
  const getIconStyle = (index, total) => {
    const isMobile = window.innerWidth < 768;
    const angle = (index / total) * Math.PI * 2 + (time * 0.2);
    // Responsive initial circle (400px desktop, 220px mobile)
    const baseRadius = isMobile ? 220 : 400;
    // Slow the scroll-to-motion ratio on mobile so the circle expands/fades gradually.
    const sp = isMobile ? scrollPos * 0.1 : scrollPos;
    const radius = baseRadius + (sp * 8);
    const rotation = (time * 15) + (index * 30) + (sp * 0.5);

    // Orbit around center
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;

    const opacity = Math.max(0, 0.8 - (sp / 1500));
    const scale = (isMobile ? 1.8 : 1.5) + (sp / 200);

    return {
      transform: `translate3d(calc(-50% + ${x}px), calc(-50% + ${y}px), 0) rotate(${rotation}deg) scale(${scale})`,
      opacity: opacity,
      display: opacity === 0 ? 'none' : 'block'
    };
  };

  // Smoother interpolation for the "Fly into Nav" effect
  const getDynamicLogoStyle = () => {
    const isMobile = window.innerWidth < 768;
    const progress = Math.min(scrollPos / 500, 1);

    const targetLeft = 5; // 5%
    const targetTop = isMobile ? 16 : 24;
    // Shift hero content up slightly on mobile so the scroll indicator sits below the currency circle
    const startCenter = isMobile ? 44 : 50;

    // Interpolate
    const currentLeft = 50 - (50 - targetLeft) * progress;
    const currentTop = startCenter - (startCenter - (targetTop / window.innerHeight * 100)) * progress;

    // Scale from massive (15vw ~ 150px) to small (1.5rem ~ 24px)
    const startSize = isMobile ? 64 : Math.max(80, window.innerWidth * 0.12);
    const endSize = 24;
    const currentSize = startSize - (startSize - endSize) * progress;

    // Interpolate translation to transition from center-aligned to top-left aligned
    const currentTranslateX = -50 + (50 * progress);
    const currentTranslateY = -50 + (50 * progress);

    return {
      position: 'fixed',
      left: progress === 1 ? `${targetLeft}%` : `${currentLeft}%`,
      top: progress === 1 ? `${targetTop}px` : `${currentTop}%`,
      transform: `translate(${currentTranslateX}%, ${currentTranslateY}%)`,
      fontSize: `${currentSize}px`,
      zIndex: 9999,
      pointerEvents: progress === 1 ? 'auto' : 'none',
      cursor: progress === 1 ? 'pointer' : 'default',
      fontWeight: '900',
      letterSpacing: progress === 1 ? '-0.5px' : (isMobile ? '-2px' : '-4px'),
    };
  };

  const aboutUsStyle = {
    opacity: Math.min(1, Math.max(0, (scrollPos - 400) / 400)),
    transform: `translateY(${Math.max(0, 100 - (scrollPos - 400) * 0.5)}px)`,
  };

  const proceedBtnStyle = (() => {
    const isMobile = window.innerWidth < 768;
    const yOffset = isMobile ? 85 : 180;
    return {
      '--btn-y-offset': `${yOffset}px`,
      opacity: Math.max(0, 1 - (scrollPos / 300)),
      transform: `translate3d(-50%, calc(-50% + ${yOffset}px + ${scrollPos * 0.2}px), 0)`,
      pointerEvents: scrollPos > 200 ? 'none' : 'auto'
    };
  })();

  if (authLoading || isAuthenticated) {
    return <LoadingScreen />;
  }

  return (
    <div className="lp-container">
      {/* Mobile Sticky Header Background */}
      {isMobileView && (
        <div 
          className="lp-mobile-nav-bg" 
          style={{ 
            opacity: Math.min(1, Math.max(0, (scrollPos - 100) / 400)),
            backdropFilter: 'blur(10px)',
            background: 'rgba(var(--lp-bg-rgb), 0.7)'
          }} 
        />
      )}

      {/* Hero Section */}
      <section className="lp-hero" ref={heroRef}>
        <SwirlBackground />
        <div className="lp-bg-icons">
          {activeCurrencies.map((symbol, i) => (
            <div key={i} className="lp-curr-icon" style={getIconStyle(i, activeCurrencies.length)}>
              {symbol}
            </div>
          ))}
        </div>

        <h1
          className="lp-logo-text"
          style={getDynamicLogoStyle()}
          ref={logoRef}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Budgetify
        </h1>

        <button
          className="lp-proceed-btn"
          style={proceedBtnStyle}
          onClick={() => setAuthModal({ show: true, type: 'signin' })}
        >
          Proceed ➔
        </button>

        <div className="lp-scroll-indicator" style={{ opacity: 1 - scrollPos / 200 }}>
          <div className="lp-mouse">
            <div className="lp-wheel"></div>
          </div>
          <p>Scroll down</p>
        </div>
      </section>

      {/* About Section */}
      <section className="lp-about" style={aboutUsStyle}>
        <div className="lp-about-content">
          <h2 className="lp-about-title">Your Finance, Your Privacy</h2>
          <p className="lp-about-text">
            Budgetify is built on a simple promise: total control. We don't crawl your bank accounts or sell your data.
            You provide the information, and we provide the tools to master it. From daily expense splits to complex 
            global stock portfolios, manage your entire financial life in one manual-first, privacy-oriented command center.
          </p>
          <div className="lp-features-grid">
            {[{
              icon: '🛡️', title: 'Privacy Oriented', text: 'We only process what you manually provide. No bank tracking, no snooping.',
              dir: { x: -1, y: -1 }
            }, {
              icon: '👥', title: 'Split Management', text: 'Easily manage group expenses, shared tabs, and split bills with friends.',
              dir: { x: 1, y: -1 }
            }, {
              icon: '💳', title: 'Credit & Loans', text: 'Keep a sharp eye on your card balances, bill cycles, and loan repayments.',
              dir: { x: -1, y: 1 }
            }, {
              icon: '📈', title: 'Investments', text: 'Track your global portfolios and investment performance.',
              dir: { x: 1, y: 1 }
            }].map((feat, i) => {
              const isMobile = window.innerWidth < 768;
              const baseStart = isMobile ? 250 : 600;
              const window_ = isMobile ? 250 : 400;
              const startScroll = baseStart + (i * (isMobile ? 30 : 50)); // Staggered start, earlier + tighter on mobile
              const progress = Math.min(1, Math.max(0, (scrollPos - startScroll) / window_));
              const offset = 100 * (1 - progress);
              const cardStyle = {
                opacity: progress,
                transform: `translate3d(${feat.dir.x * offset}px, ${feat.dir.y * offset}px, 0) scale(${0.8 + 0.2 * progress})`,
              };
              return (
                <div key={i} className="lp-feature-card" style={cardStyle}>
                  <span className="lp-feat-icon">{feat.icon}</span>
                  <h3>{feat.title}</h3>
                  <p>{feat.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {authModal.show && (
        <AuthModal
          type={authModal.type}
          onClose={() => setAuthModal({ ...authModal, show: false })}
          onSwitch={(nextType) => {
            if (nextType) {
              setAuthModal({ ...authModal, type: nextType });
            } else {
              setAuthModal({ ...authModal, type: authModal.type === 'signin' ? 'signup' : 'signin' });
            }
          }}
        />
      )}

      <section className="lp-cta">
        <h2>Questions or Feedback?</h2>
        <p>We're building Budgetify for you. We'd love to hear your thoughts.</p>
        
        <form className="lp-contact-form" onSubmit={(e) => { e.preventDefault(); alert('Message sent! We will get back to you soon.'); }}>
          <div className="lp-form-row">
            <input type="text" placeholder="Your Name" required />
            <input type="email" placeholder="Email Address" required />
          </div>
          <textarea placeholder="Your Message" rows="4" required></textarea>
          <button type="submit" className="lp-cta-btn">Send Message</button>
        </form>
      </section>

      <footer className="lp-footer">
        <p>&copy; 2026 Budgetify. Built for Financial Freedom.</p>
      </footer>
    </div>
  );
}
